import React, { useContext, useState } from "react";
import { Box } from "Components/Base/Layout/Box";
import config from "lib/config";
import moment from "moment";
import {
  PROP_TYPE_PROGRAM_COURSE,
  PROP_DEFAULT_PROGRAM_COURSE,
} from "../../../../propTypes";
import LangContext from "../../../../langContext";
import {
  OBJECT_TYPE_CLASSROOM,
  ATTEST_WAITING,
} from "../../../../constants.js";
import ProgramContext from "../../ProgramContext";
import Description from "../../../Description";
import {
  formatCourseDuration,
  GetRequestedCourseLink,
  StyledEmptyProgress,
  StyledEmptyTag,
  StyledEmptyStars,
  StyledEmptyDescription,
} from "../../../../functions";
import ModalCourseDetails from "lib/components/MyCoursesCardView/courseDetails";
import Rating from "lib/components/Rating";
import { sanitize } from "lib/sanitizer.js";
import { AddLinebreaksRemoveHtmlTags } from "lib/utils";

const WarningTag = ({ course, languageContext }) => {
  if (
    course.requested ||
    (!course.completed && course.attestStatus === "ATTEST_WAITING")
  ) {
    return (
      <span className="tag-warning">
        {course.requested
          ? languageContext("courserequested")
          : languageContext("waitingforattest")}
      </span>
    );
  }
  return <StyledEmptyTag className="card-empty-tag" />;
};

const ProgramTypeClassroom = (props) => {
  const [displayCourseDetails, setDisplayCourseDetails] = useState(false);
  const languageContext = useContext(LangContext);
  const ProgramSettings = useContext(ProgramContext);
  const { course, activityId, isPortalOverview, isPreview } = props;

  const [rating, setRating] = useState(course.personalRating.rating);
  const [comment, setComment] = useState(course.personalRating.comment);

  const displayCourseDates = false;
  const courseDates = {
    startDate: course.courseStarts,
    endDate: course.courseEnds,
  };
  const displayTime = formatCourseDuration(course);
  const description = course.stepDescription || course.itemDescription;
  const displayDescription =
    course.itemDescription &&
    course.itemDescription !== "" &&
    !isPortalOverview;
  const displayRating =
    course.allowRating && !course.requested && !isPortalOverview;

  const updateRating = (newRating, newComment) => {
    course.personalRating.rating = newRating;
    course.personalRating.comment = newComment;
    setRating(newRating);
    setComment(newComment);
  };

  return (
    <div>
      {isPortalOverview && (
        <Rating
          currentRating={course.avgRating}
          noOfRatings={course.noOfRatings}
          readOnly={true}
        />
      )}
      {displayRating ? (
        <Rating
          currentRating={course.personalRating.rating || 0}
          currentComment={course.personalRating.comment}
          type={OBJECT_TYPE_CLASSROOM}
          objectId={course.courseObjectId}
          updateRating={updateRating}
          readOnly={false}
          managerView={ProgramSettings.managerView}
        />
      ) : (
        !isPortalOverview && <StyledEmptyStars />
      )}
      {displayCourseDates && <ProgramTypeClassroomDates dates={courseDates} />}
      {displayDescription ? (
        <Description>
          <span
            title={AddLinebreaksRemoveHtmlTags(sanitize(description))}
            dangerouslySetInnerHTML={{
              __html: sanitize(description),
            }}
          />
        </Description>
      ) : (
        !isPortalOverview && <StyledEmptyDescription />
      )}
      {isPortalOverview && (
        <div>
          <p className="elipssis">
            {`${languageContext("reason")}: `}
            <br />
            <span title={course.reason}>{course.reason}</span>
          </p>
        </div>
      )}

      {!isPortalOverview && (
        <WarningTag course={course} languageContext={languageContext} />
      )}

      {!isPortalOverview && <StyledEmptyProgress />}

      <Box
        display="flex"
        justifyContent={displayTime ? "space-between" : "center"}
        width="100%"
      >
        {displayTime && (
          <span>
            <span className="icon-time" aria-hidden="true" />
            &nbsp;{displayTime}
          </span>
        )}

        {course.isActiveStep &&
          !course.requested &&
          !isPortalOverview &&
          !isPreview && (
            <div>
              <button
                onClick={() => setDisplayCourseDetails(true)}
                className="button-transparent link"
              >
                {languageContext("showactivity")}
              </button>
            </div>
          )}

        {isPortalOverview && (
          <GetRequestedCourseLink
            courseId={course.courseObjectId}
            activityId={activityId}
            label={languageContext("showactivity")}
            isPortalOverview={isPortalOverview}
          />
        )}

        {course.requested &&
          !ProgramSettings.managerView &&
          !isPortalOverview && (
            <GetRequestedCourseLink
              courseId={course.courseObjectId}
              activityId={activityId}
              label={languageContext("registerHere")}
            />
          )}

        {displayCourseDetails && !isPortalOverview && (
          <CourseDetails
            course={course}
            onClose={setDisplayCourseDetails}
            activityId={activityId}
          />
        )}
      </Box>
    </div>
  );
};

const CourseDetails = (props) => {
  const ProgramSettings = useContext(ProgramContext);
  const { course, activityId } = props;
  let classroomCourse = course;
  classroomCourse.courseId = course.courseObjectId;

  return (
    <ModalCourseDetails
      initiallyOpen
      title={course.title}
      dialogStyles={{ minWidth: "450px" }}
      course={classroomCourse}
      closeDetails={() => props.onClose(false)}
      personalId={ProgramSettings.userId}
      activityId={activityId}
      isManagerOrAdmin={ProgramSettings.managerView}
    ></ModalCourseDetails>
  );
};

const ProgramTypeClassroomDates = (props) => {
  const languageContext = useContext(LangContext);
  const { startDate, endDate } = props.dates;
  const dateFormat =
    config && config.shortDateFormat
      ? config.shortDateFormat.toUpperCase()
      : "yyyy-MM-dd HH:mm";

  return (
    <div className="card-dates">
      {moment().isAfter(startDate) && (
        <span>
          {`${languageContext("enddate")}: ${moment(endDate).format(
            dateFormat,
          )}`}
        </span>
      )}
      {moment().isBefore(startDate) && (
        <span>
          {`${languageContext("startdate")}: ${moment(startDate).format(
            dateFormat,
          )}`}
        </span>
      )}
    </div>
  );
};

ProgramTypeClassroom.propTypes = {
  course: PROP_TYPE_PROGRAM_COURSE,
};

ProgramTypeClassroom.defaultProps = {
  course: PROP_DEFAULT_PROGRAM_COURSE,
};

export default ProgramTypeClassroom;
